import type { DeepTodo, DeepTodoStatus } from "@/stores/ai-explorer-store"

/**
 * Snapshot todos may also carry the legacy `"done"` status from older
 * persisted sessions written before the enum was aligned to the BE wire
 * format ("completed"). Treat both as equivalent on input; emit only
 * "completed" downstream.
 */
type SnapshotTodo = Omit<DeepTodo, "id" | "status"> & {
  id?: string
  status: DeepTodoStatus | "done"
}

type MergeDeepTodosOptions = {
  previous: readonly DeepTodo[]
  incoming: readonly SnapshotTodo[]
}

function todoLabel(todo: Pick<DeepTodo, "content" | "text">): string {
  return String(todo.content || todo.text || "")
}

function hashTodoLabel(label: string): string {
  let hash = 0
  for (let i = 0; i < label.length; i += 1) {
    hash = (hash * 31 + label.charCodeAt(i)) >>> 0
  }
  return hash.toString(36)
}

function normalizeStatus(status: SnapshotTodo["status"]): DeepTodoStatus {
  // Legacy "done" → "completed" (canonical wire value). Other statuses
  // pass through unchanged.
  return status === "done" ? "completed" : status
}

function todoId(todo: SnapshotTodo, index: number): string {
  if (todo.id) return todo.id
  return `todo-${index}-${hashTodoLabel(todoLabel(todo))}`
}

type PreviousTodoMatch = {
  previous: readonly DeepTodo[]
  id: string
  label: string
  index: number
}

function findPreviousTodo({ previous, id, label, index }: PreviousTodoMatch): DeepTodo | undefined {
  return (
    previous.find((todo) => todo.id === id) ??
    previous.find((todo, previousIndex) => previousIndex === index && todoLabel(todo) === label) ??
    previous.find((todo) => todoLabel(todo) === label)
  )
}

export function mergeDeepTodosForSnapshot({
  previous,
  incoming,
}: MergeDeepTodosOptions): DeepTodo[] {
  const now = new Date().toISOString()

  return incoming.map((todo, index) => {
    const id = todoId(todo, index)
    const label = todoLabel(todo)
    const prior = findPreviousTodo({ previous, id, label, index })
    const status = normalizeStatus(todo.status)

    const startedAt =
      todo.started_at ??
      prior?.started_at ??
      (status === "in_progress" || status === "completed" ? now : null)

    const completedAt =
      todo.completed_at ?? prior?.completed_at ?? (status === "completed" ? now : null)

    return {
      ...todo,
      id,
      status,
      started_at: startedAt,
      completed_at: completedAt,
    }
  })
}
